import React, { useEffect, useState } from 'react';
import { AlertTriangle, User, Clock, DollarSign, ChevronRight, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useCustomerStore from '../../stores/useCustomerStore';

/**
 * Overdue Customers Widget
 * ويدجت العملاء المتأخرين في السداد
 *
 * Displays customers who have exceeded their payment terms
 * Shows on dashboard home page as an alert widget
 *
 * Protocols Applied:
 * - P-COMPONENT-001: Clean Component Structure
 * - P-RTL-001: RTL Support
 * - P-DARK-MODE-001: Dark Mode Support
 * - P-UX-001: Loading States
 * - P010: Error Handling
 */

const OverdueCustomersWidget = () => {
  const navigate = useNavigate();
  const { fetchOverdueCustomers, loading, error } = useCustomerStore();
  const [overdueCustomers, setOverdueCustomers] = useState([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    loadOverdueCustomers();
  }, []);

  const loadOverdueCustomers = async () => {
    try {
      const customers = await fetchOverdueCustomers();
      setOverdueCustomers(customers);
    } catch (err) {
      console.error('Failed to load overdue customers:', err);
    }
  };

  const handleViewAll = () => {
    navigate('/customers', { state: { filter: 'overdue' } });
  };

  const handleViewCustomer = (customerId) => {
    navigate(`/customers/${customerId}`);
  };

  const totalOverdueAmount = overdueCustomers.reduce(
    (sum, customer) => sum + parseFloat(customer.current_balance || 0),
    0
  );

  // Show only top 3 customers by default
  const displayedCustomers = expanded ? overdueCustomers : overdueCustomers.slice(0, 3);

  // If no overdue customers after loading, show empty state instead of hiding
  const hasData = overdueCustomers.length > 0;

  return (
    <div className="bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border-2 border-red-200 dark:border-red-800 rounded-2xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-orange-600 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">
                عملاء متأخرين في السداد
              </h3>
              <p className="text-xs text-white/80">
                {overdueCustomers.length} عميل
              </p>
            </div>
          </div>
          <div className="text-left">
            <div className="text-sm text-white/80">إجمالي المتأخر</div>
            <div className="text-xl font-bold text-white">
              {totalOverdueAmount.toLocaleString('ar-KW', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} د.ك
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          </div>
        ) : error ? (
          <div className="text-center py-4 text-red-600 dark:text-red-400">
            {error}
          </div>
        ) : !hasData ? (
          <div className="text-center py-8">
            <div className="bg-green-100 dark:bg-green-900/30 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <p className="text-gray-600 dark:text-gray-400 font-medium">
              ممتاز! لا يوجد عملاء متأخرين في السداد
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
              جميع عملائك ملتزمون بالدفع في الوقت المحدد
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {displayedCustomers.map((customer) => (
              <div
                key={customer.id}
                onClick={() => handleViewCustomer(customer.id)}
                className="bg-white dark:bg-gray-800 rounded-xl p-3 cursor-pointer hover:shadow-md transition-all duration-200 hover:scale-[1.02] border border-red-100 dark:border-red-900"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="bg-red-100 dark:bg-red-900/30 p-2 rounded-lg">
                      <User className="w-5 h-5 text-red-600 dark:text-red-400" />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {customer.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {customer.code} • {customer.phone}
                      </div>
                    </div>
                  </div>
                  <div className="text-left">
                    <div className="text-lg font-bold text-red-600 dark:text-red-400">
                      {parseFloat(customer.current_balance || 0).toLocaleString('ar-KW', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} د.ك
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                      <Clock className="w-3 h-3" />
                      <span>متأخر {customer.overdue_days} يوم</span>
                    </div>
                  </div>
                </div>

                {/* Credit Status Badge */}
                <div className="mt-2 flex items-center gap-2">
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      customer.credit_status === 'danger'
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                        : customer.credit_status === 'warning'
                        ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                        : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                    }`}
                  >
                    استخدام {customer.credit_usage_percentage || 0}% من الحد الائتماني
                  </span>
                </div>
              </div>
            ))}

            {/* Expand/Collapse Button */}
            {overdueCustomers.length > 3 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="w-full py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              >
                {expanded ? 'عرض أقل' : `عرض المزيد (${overdueCustomers.length - 3}+)`}
              </button>
            )}

            {/* View All Button */}
            <button
              onClick={handleViewAll}
              className="w-full mt-2 flex items-center justify-center gap-2 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white rounded-xl py-3 font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-95"
            >
              <span>عرض كل العملاء المتأخرين</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OverdueCustomersWidget;
